import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Skeleton,
  Alert,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  ComposedChart,
  AreaChart,
  Area,
  CartesianGrid,
  ScatterChart,
  Scatter,
} from "recharts";
import EmployeeKpiCard from "./EmployeeKpiCard";
import axios from "axios";
import { BASE_URL } from "app/services/auth-services";
import { todayDate } from "app/shared/ReuseComponents/DateFormatter";
import { getWeekNumber } from "app/shared/ReuseComponents/StyledComponents";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import {
  useGetHolidayYearwiseListQuery,
  useGetMonthwiseCheckinListQuery,
  useGetTimesheetForMonthlyQuery,
} from "app/services/RTK_Apis/dashbords/EmployeeDashboardApi";
import { useGetEmployeeLeaveBalanceQuery } from "app/services/RTK_Apis/EmployeeManagement/viewEmployeeApi";

const COLORS = ["#8884d8", "#82ca9d", "#ffc658", "#ff8042"];

const sampleData = {
  attendance: "22/30",
  leave_balance: "12 Days",
  pending_timesheets: 1,
  payslip: "₹60,000",
  feedback_received: 3,
  assigned_trainings: 2,

  attendance_trend: [
    { date: "01 Sep", status: 1 },
    { date: "02 Sep", status: 1 },
    { date: "03 Sep", status: 0 },
    { date: "04 Sep", status: 1 },
    { date: "05 Sep", status: 1 },
    { date: "06 Sep", status: 1 },
    { date: "07 Sep", status: 0 },
  ],

  leave_types: [
    { name: "Sick Leave", value: 4 },
    { name: "Casual Leave", value: 6 },
    { name: "Paid Leave", value: 2 },
  ],

  timesheet_status: [
    { week: "Week 1", submitted: 1 },
    { week: "Week 2", submitted: 1 },
    { week: "Week 3", submitted: 0 },
    { week: "Week 4", submitted: 1 },
  ],

  training_progress: [
    { name: "Completed", value: 1 },
    { name: "Pending", value: 1 },
  ],
};

const EmployeeDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const token = localStorage.getItem("accesstoken");
  const userId = parseInt(localStorage.getItem("UserId"));
  const companyId = parseInt(localStorage.getItem("OrganizationId"));
  const partnerId = parseInt(localStorage.getItem("PartnerId"));
  const employeeId = parseInt(localStorage.getItem("EmployeeId"));
  const employeeNo = localStorage.getItem("EmployeeNo");

  const header = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  };

  let userDetails = localStorage.getItem("UserDetails");
  userDetails = JSON.parse(userDetails);

  const year = new Date().getFullYear();
  const month = new Date().toLocaleString("default", { month: "long" });
  const weekNo = getWeekNumber();
  const totalDays = new Date(year, new Date().getMonth() + 1, 0).getDate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const { data: attendanceData = [] } = useGetMonthwiseCheckinListQuery({
    partnerId,
    employeeId,
    year,
    month,
  });

  const { data: leaveBalanceData = [] } = useGetEmployeeLeaveBalanceQuery({
    companyId,
    employeeId,
    year,
  });

  const { data: timesheetData = [] } = useGetTimesheetForMonthlyQuery({
    partnerId,
    employeeId,
    month,
    year,
  });

  const { data: holidayDataRaw = [] } = useGetHolidayYearwiseListQuery({
    partnerId,
    year,
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const results = await Promise.allSettled([
          axios.get(
            `${BASE_URL}/Felix/Monthwisecheckinlist/?Partner_Id=${partnerId}&Employee_Id=${employeeId}&Year=${year}&Month=${month}`,
            header,
          ),
          axios.get(
            `${BASE_URL}/Felix/GetEmployeeLeaveBalance/?Company_Id=${companyId}&Employee_Id=${employeeId}&Year=${year}`,
            header,
          ),
          axios.get(
            `${BASE_URL}/Felix/TimesheetForMonthly/?Partner_Id=${partnerId}&Employee_Id=${employeeId}&Month=${month}&Year=${year}`,
            header,
          ),
          axios.get(
            `${BASE_URL}/Felix/holidayyearwiselist/?Partner_Id=${partnerId}&Year=${year}`,
            header,
          ),
        ]);

        const [attendanceRes, leaveBalanceRes, timesheetRes, holidayRes] =
          results;

        const attendanceData =
          attendanceRes.status === "fulfilled" ? attendanceRes.value.data : [];

        const leaveBalanceData =
          leaveBalanceRes.status === "fulfilled"
            ? leaveBalanceRes.value.data
            : [];

        const timesheetData =
          timesheetRes.status === "fulfilled" ? timesheetRes.value.data : [];

        const holidayData =
          holidayRes.status === "fulfilled"
            ? holidayRes.value.data?.[0]?.Holidays
            : [];

        setData({
          attendance_trend: attendanceData,
          attendance: `${attendanceData.length} / ${totalDays}`,
          leaveBalanceData,
          timesheetData,
          holidayData,
        });
      } catch (err) {
        // This will almost never run now
        console.error("Unexpected error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Process holidays data for chart
  const holidayCountsByMonth = data?.holidayData?.reduce((acc, holiday) => {
    const month = new Date(holiday.date).toLocaleString("default", {
      month: "short",
    });
    acc[month] = (acc[month] || 0) + 1;
    return acc;
  }, {});

  const chartData = holidayCountsByMonth
    ? Object.entries(holidayCountsByMonth).map(([month, count]) => ({
        month,
        holidays: count,
      }))
    : [];

  const attendanceChartData = data?.attendance_trend?.map((item) => ({
    date: item.Date.slice(8, 10), // 02, 03
    fullDate: item.Date,
    hours: item.Present_Flag ? item.Working_Hours : 0,
    status: item.Present_Flag ? "Present" : "Absent",
  }));

  const weeklyTimesheetData = React.useMemo(() => {
    if (!data?.timesheetData) return [];

    const weekMap = {};

    data?.timesheetData?.forEach((entry) => {
      const week = `Week ${entry.Weekno || "N/A"}`;

      if (!weekMap[week]) {
        weekMap[week] = {
          week,
          Approved: 0,
          Submitted: 0,
          Pending: 0,
          Rejected: 0,
        };
      }

      weekMap[week][entry.Timesheet_flg] += 1;
    });

    return Object.values(weekMap);
  }, [data?.timesheetData]);

  const pendingCount = weeklyTimesheetData.reduce(
    (total, item) => total + (item?.Pending || 0),
    0,
  );

  // Loading state
  // Loading state
  if (loading) {
    return (
      <DashboardSkeleton isMobile={isMobile} isSmallMobile={isSmallMobile} />
    );
  }

  if (!data) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        No data available. Please try again later.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        p: isMobile ? 1 : 2,
        background: "#f5f7fb",
        minHeight: "100vh",
      }}
    >
      <Typography
        variant={isMobile ? "h5" : "h4"}
        gutterBottom
        sx={{
          fontWeight: 600,
          mb: 2,
          textAlign: "left",
        }}
      >
        Employee Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={isSmallMobile ? 1 : 2} mb={4}>
        <Grid item xs={12} sm={3}>
          <EmployeeKpiCard
            title="Attendance"
            value={data?.attendance || 0}
            icon={<AccessTimeFilledIcon />}
            color="#22c55e"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <EmployeeKpiCard
            title="Leave Balance"
            value={data?.leaveBalanceData?.Total_Closing_Balance || 0}
            icon={<EventAvailableIcon />}
            color="#3b82f6"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <EmployeeKpiCard
            title="Pending Timesheets"
            value={pendingCount || 0}
            icon={<PendingActionsIcon />}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <EmployeeKpiCard
            title="Holidays"
            value={data?.holidayData?.length || 0}
            icon={<BeachAccessIcon />}
            color="#a855f7"
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Attendance Trend Line Chart */}
        <Grid item xs={12} lg={6}>
          <ChartBox
            title="Daily Attendance"
            isMobile={isMobile}
            isSmallMobile={isSmallMobile}
          >
            {!attendanceChartData?.length ? (
              <NoDataFound height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={attendanceChartData} barCategoryGap={20}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />

                  <XAxis dataKey="date" fontSize={12} />

                  <YAxis
                    label={{
                      value: "Hours",
                      angle: -90,
                      position: "insideLeft",
                    }}
                    fontSize={12}
                    domain={[0, 12]}
                  />

                  <Tooltip
                    content={({ payload }) => {
                      if (!payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div
                          style={{
                            background: "#fff",
                            padding: 10,
                            borderRadius: 8,
                          }}
                        >
                          <strong>{d.fullDate}</strong>
                          <div>Status: {d.status}</div>
                          <div>Hours: {d.hours}</div>
                        </div>
                      );
                    }}
                  />

                  <Bar dataKey="hours" radius={[6, 6, 0, 0]}>
                    {attendanceChartData?.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={
                          entry.status === "Present" ? "#4CAF50" : "#F44336"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartBox>
        </Grid>

        {/* Leave Usage Breakdown Pie Chart */}
        <Grid item xs={12} lg={6}>
          <ChartBox
            title="Leave Usage Breakdown"
            isMobile={isMobile}
            isSmallMobile={isSmallMobile}
          >
            {!data?.leaveBalanceData?.Leave_Breakdown?.length ? (
              <NoDataFound height={300} />
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isSmallMobile ? 250 : 300}
              >
                <PieChart>
                  <Pie
                    data={data?.leaveBalanceData?.Leave_Breakdown}
                    dataKey="Taken_Leaves"
                    nameKey="Leave_Type"
                    outerRadius={isSmallMobile ? 80 : 100}
                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {data?.leaveBalanceData?.Leave_Breakdown?.map(
                      (entry, index) => (
                        <Cell
                          key={`cell-leave-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ),
                    )}
                  </Pie>
                  <Tooltip
                    wrapperStyle={{ fontSize: isSmallMobile ? 12 : 14 }}
                  />
                  <Legend
                    wrapperStyle={{
                      fontSize: isSmallMobile ? 10 : 12,
                      paddingTop: isSmallMobile ? 10 : 15,
                    }}
                    layout={isSmallMobile ? "horizontal" : "vertical"}
                    verticalAlign={isSmallMobile ? "bottom" : "middle"}
                    align={isSmallMobile ? "center" : "right"}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartBox>
        </Grid>

        {/* Timesheet Submission Bar Chart */}
        <Grid item xs={12} lg={6}>
          <ChartBox
            title={`Timesheet Submission Status`}
            isMobile={isMobile}
            isSmallMobile={isSmallMobile}
          >
            {!weeklyTimesheetData?.length ? (
              <NoDataFound height={280} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={weeklyTimesheetData}
                  barCategoryGap={20}
                  barGap={6}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />

                  <XAxis dataKey="week" fontSize={12} />

                  <YAxis allowDecimals={false} fontSize={12} />

                  <Tooltip
                    content={({ payload }) => {
                      if (!payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div
                          style={{
                            background: "#fff",
                            padding: 10,
                            borderRadius: 8,
                          }}
                        >
                          <strong>{d.week}</strong>
                          <div>Approved: {d.Approved}</div>
                          <div>Submitted: {d.Submitted}</div>
                          <div>Pending: {d.Pending}</div>
                          <div>Rejected: {d.Rejected}</div>
                        </div>
                      );
                    }}
                  />

                  <Legend />

                  <Bar
                    dataKey="Approved"
                    fill="#4CAF50"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="Submitted"
                    fill="#2196F3"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar dataKey="Pending" fill="#FFC107" radius={[4, 4, 0, 0]} />
                  <Bar
                    dataKey="Rejected"
                    fill="#F44336"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartBox>
        </Grid>

        {/* Holidays Chart */}
        <Grid item xs={12} lg={6}>
          <ChartBox
            title="Holidays by Month"
            isMobile={isMobile}
            isSmallMobile={isSmallMobile}
          >
            {!chartData?.length ? (
              <NoDataFound height={300} />
            ) : (
              <ResponsiveContainer
                width="100%"
                height={isSmallMobile ? 250 : 300}
              >
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" fontSize={isSmallMobile ? 10 : 12} />
                  <YAxis fontSize={isSmallMobile ? 10 : 12} />
                  <Tooltip
                    wrapperStyle={{ fontSize: isSmallMobile ? 12 : 14 }}
                  />
                  <Bar dataKey="holidays" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartBox>
        </Grid>
      </Grid>
    </Box>
  );
};

// Enhanced ChartBox component with mobile responsiveness
const ChartBox = ({ title, children, isMobile, isSmallMobile }) => (
  <Card
    sx={{
      borderRadius: 2,
      width: "100%",
      height: "100%",
      backgroundColor: "background.paper",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      transition: "all 0.3s ease",
      "&:hover": {
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
        transform: isMobile ? "none" : "translateY(-2px)",
      },
      display: "flex",
      flexDirection: "column",
    }}
  >
    <CardContent
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        p: isSmallMobile ? 1.5 : 2,
        "&:last-child": { pb: isSmallMobile ? 1.5 : 2 },
      }}
    >
      <Typography
        variant={isSmallMobile ? "h6" : "h5"}
        fontWeight={500}
        gutterBottom
        sx={{
          fontSize: isSmallMobile ? "0.8rem" : "1rem",
          textAlign: isSmallMobile ? "center" : "left",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: isSmallMobile ? 250 : 300 }}>
        {children}
      </Box>
    </CardContent>
  </Card>
);

const NoDataFound = ({ height = 260 }) => (
  <Box
    sx={{
      height,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      borderRadius: 2,
      background: "#fafafa",
      border: "1px dashed #e5e7eb",
    }}
  >
    <Typography
      sx={{
        fontSize: "0.9rem",
        fontWeight: 500,
        color: "#475569",
        mb: 0.5,
      }}
    >
      No data available
    </Typography>

    <Typography
      sx={{
        fontSize: "0.75rem",
        color: "#94a3b8",
      }}
    >
      Data will appear here once available
    </Typography>
  </Box>
);
const DashboardSkeleton = ({ isMobile, isSmallMobile }) => {
  return (
    <Box
      sx={{
        p: isMobile ? 1.5 : 3,
        background: "#f5f7fb",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box mb={3}>
        <Skeleton width={260} height={40} />
        <Skeleton width={180} height={20} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={4}>
        {[1, 2, 3, 4].map((item) => (
          <Grid item xs={12} sm={6} md={6} lg={3} key={item}>
            <Card
              sx={{
                borderRadius: 3,
                p: 2,
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
              }}
            >
              <Box display="flex" justifyContent="space-between">
                <Box>
                  <Skeleton width={90} height={18} />
                  <Skeleton width={70} height={32} sx={{ mt: 1 }} />
                </Box>

                <Skeleton variant="circular" width={40} height={40} />
              </Box>

              <Skeleton width="60%" height={14} sx={{ mt: 2 }} />
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((item) => (
          <Grid item xs={12} sm={12} md={6} key={item}>
            <Card
              sx={{
                borderRadius: 3,
                p: 2,
                height: "100%",
                boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
              }}
            >
              {/* Title */}
              <Skeleton width="50%" height={24} sx={{ mb: 2 }} />

              {/* Fake chart lines (more realistic) */}
              <Box
                sx={{
                  height: isSmallMobile ? 220 : 260,
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 1,
                }}
              >
                {[40, 60, 30, 80, 50, 70, 45].map((h, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    width="100%"
                    height={`${h}%`}
                    sx={{ borderRadius: 2 }}
                  />
                ))}
              </Box>

              {/* Legend */}
              <Box mt={2} display="flex" gap={2}>
                <Skeleton width={60} height={14} />
                <Skeleton width={60} height={14} />
                <Skeleton width={60} height={14} />
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default EmployeeDashboard;
